'use strict'

import animations from '../utility/animations.js'

const init = () => {
    const videoWrappers = document.querySelectorAll('.jonnitto-prettyembed--video')
    const isBackend = document.body.classList.contains('neos-backend')

    videoWrappers.forEach((videoWrapper) => {
        let video = videoWrapper.querySelector('video')
        const videoPreview = videoWrapper.querySelector('.jonnitto-prettyembed-preview')
        let customVideoControls = videoWrapper.querySelector('.custom-video-controls')
        let tcPlayButton = videoWrapper.querySelector('.tc-play-button')

        let hasLightbox = false
        let lightboxWrapper = null

        if (videoWrapper.classList.contains('has-lightbox')) {
            const identifier = videoWrapper.id;
            hasLightbox = true
            lightboxWrapper = document.querySelector('#lightbox-' + identifier);

            video = lightboxWrapper.querySelector('video');
            customVideoControls = lightboxWrapper.querySelector('.custom-video-controls')
            tcPlayButton = lightboxWrapper.querySelector('.tc-play-button')
        }

        if (!video) return

        // if video is currently playing, be sure video preview is hidden
        if (!video.paused) {
            videoPreview.style.display = 'none'
        }

        if (hasLightbox && lightboxWrapper) {
            lightboxWrapper.classList.add('has-been-played')
        }

        // helper: format seconds into mm:ss
        const formatTime = (time) => {
            const safe = Number.isFinite(time) && time > 0 ? time : 0
            const minutes = Math.floor(safe / 60)
            const seconds = Math.floor(safe % 60).toString().padStart(2, '0')
            return `${minutes}:${seconds}`
        }

        const setPlayingClass = (playing) => {
            if (!customVideoControls) return
            customVideoControls.classList.toggle('is-playing', playing)
        }

        const togglePlayPause = () => {
            if (isBackend) return
            if (video.paused || video.classList.contains('paused')) {
                video.play().catch((error) => console.error(error))
                tcPlayButton?.classList.add('hidden')
                if (hasLightbox) {
                    lightboxWrapper.classList.add('has-been-played')
                } else {
                    videoWrapper.classList.add('has-been-played')
                }
            } else {
                video.pause()
                tcPlayButton?.classList.remove('hidden')
            }
        }

        // autoplay handler on scroll if video has autoplay attribute
        if (video.autoplay && video.paused) {
            animations.playVideoAutoplayOnScroll(video)
        }

        // overlay play button
        if (tcPlayButton) {
            tcPlayButton.addEventListener('click', togglePlayPause)
        }

        // allow clicking on video or preview to play/pause
        video.addEventListener('click', togglePlayPause)
        videoPreview?.addEventListener('click', togglePlayPause)

        // sync playing class with video state
        video.addEventListener('play', () => setPlayingClass(true))
        video.addEventListener('pause', () => setPlayingClass(false))

        // custom controls setup
        if (customVideoControls) {
            const bottomControls = customVideoControls.querySelector('.bottom')
            const rightControls = customVideoControls.querySelector('.right-controls')

            if (bottomControls) {
                const playPauseButton = customVideoControls.querySelector('.play-pause')
                const timer = customVideoControls.querySelector('.video-timer')
                const seekBar = customVideoControls.querySelector('.seek-bar')
                const muteButton = customVideoControls.querySelector('.mute')
                const volumeControl = customVideoControls.querySelector('.volume-control')
                const fullscreenButton = customVideoControls.querySelector('.fullscreen')

                // update volume icon classes depending on slider value
                const handleVolumeClass = () => {
                    if (!rightControls || !volumeControl) return
                    const v = parseFloat(volumeControl.value ?? '1')
                    rightControls.classList.toggle('volume-up', v >= 0.5)
                    rightControls.classList.toggle('volume-down', v > 0 && v < 0.5)
                    rightControls.classList.toggle('volume-off', v === 0)
                }

                // initialize volume slider and UI
                if (volumeControl) {
                    if (video.muted) {
                        volumeControl.value = '0'
                    } else {
                        volumeControl.value = String(video.volume ?? 1)
                    }
                    handleVolumeClass()
                }

                // throttle to reduce UI updates
                let lastUpdate = 0
                let rafId = null
                let lastText = ''

                const updateUI = () => {
                    rafId = null

                    const hasDuration = Number.isFinite(video.duration) && video.duration > 0
                    const current = formatTime(video.currentTime)
                    const total = hasDuration ? formatTime(video.duration) : '--:--'
                    const newText = `${current} / ${total}`

                    if (timer && newText !== lastText) {
                        timer.textContent = newText
                        lastText = newText
                    }

                    if (seekBar && hasDuration) {
                        const percent = (video.currentTime / video.duration) * 100
                        seekBar.value = String(Math.max(0, Math.min(100, percent)))
                    }
                }

                const scheduleUpdate = () => {
                    // throttle at ~250 ms
                    const now = performance.now()
                    if (now - lastUpdate < 250) return
                    lastUpdate = now
                    if (rafId == null) rafId = requestAnimationFrame(updateUI)
                }

                // update timer/seek on playback events
                video.addEventListener('timeupdate', scheduleUpdate)
                video.addEventListener('seeking', scheduleUpdate)
                video.addEventListener('seeked', scheduleUpdate)
                video.addEventListener('ratechange', scheduleUpdate)
                video.addEventListener('volumechange', scheduleUpdate)
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'visible') scheduleUpdate()
                })

                // initialize timer and seek bar on metadata load
                video.addEventListener('loadedmetadata', () => {
                    if (timer) {
                        const total = Number.isFinite(video.duration) && video.duration > 0
                            ? formatTime(video.duration)
                            : '--:--'
                        timer.textContent = `00:00 / ${total}`
                        lastText = timer.textContent
                    }
                    if (seekBar) seekBar.value = '0'
                })

                // play/pause button handler
                playPauseButton?.addEventListener('click', togglePlayPause)

                // scrub through video with seek bar
                if (seekBar) {
                    seekBar.addEventListener('input', () => {
                        const hasDuration = Number.isFinite(video.duration) && video.duration > 0
                        if (!hasDuration) return
                        const value = parseFloat(seekBar.value || '0')
                        video.currentTime = (value / 100) * video.duration
                        scheduleUpdate()
                    })
                }

                // mute/unmute handler
                muteButton?.addEventListener('click', () => {
                    video.muted = !video.muted
                    if (volumeControl) {
                        volumeControl.value = video.muted ? '0' : (video.volume?.toString() ?? '1')
                        handleVolumeClass()
                    }
                })

                // volume slider handler
                volumeControl?.addEventListener('input', () => {
                    const v = Math.max(0, Math.min(1, parseFloat(volumeControl.value || '1')))
                    video.volume = v
                    video.muted = v === 0
                    handleVolumeClass()
                })

                // fullscreen handler
                const wrapper = videoWrapper
                const goFullscreen = () => {
                    // Safari handling
                    if (video.webkitEnterFullscreen) {
                        video.webkitEnterFullscreen()
                    }
                    // otherwise, use standard API on wrapper
                    else if (wrapper.requestFullscreen) {
                        wrapper.requestFullscreen()
                    }
                    else if (wrapper.webkitRequestFullscreen) {
                        wrapper.webkitRequestFullscreen()
                    }
                    else if (wrapper.msRequestFullscreen) {
                        wrapper.msRequestFullscreen()
                    }
                }

                const exitFullscreen = () => {
                    if (document.exitFullscreen) {
                        document.exitFullscreen()
                    }
                    else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen()
                    }
                    else if (document.msExitFullscreen) {
                        document.msExitFullscreen()
                    }
                }

                document.addEventListener('fullscreenchange', () => {
                    wrapper.classList.toggle('is-fullscreen', !!document.fullscreenElement)
                })

                // sur ton bouton fullscreen custom :
                fullscreenButton.addEventListener('click', () => {
                    if (document.fullscreenElement) exitFullscreen()
                    else goFullscreen()
                })

                // initial UI render
                scheduleUpdate()
            }
        }
    })
}

export default {init}
